import { Grid, Typography } from "@mui/material";
import { OLPaper } from "OLPaper";
import { useAppSelector } from "app/hooks";
import { RequiredFieldLabel } from "components/FormControls/RequiredFieldLabel";
import { TextInput } from "components/FormControls/TextInput";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { useMessage } from "hooks/intl";
import { selectCurrentStoreData } from "reduxState/currentStoreData/currentStoreDataSlice";
import { selectDefaultLanguageData } from "reduxState/translations/translationsSlice";
import { ConditionType, TierDetailsType } from "types/tierSets";
import { useStyles } from "../TiersForm.styles";

interface Props {
  activeTier: number;
  conditions: ConditionType[];
  tiers: TierDetailsType[];
  isExpandedView: boolean;
  setExpandedView: (val: boolean) => void;
  errors?: any;
  isEdit?: boolean;
}

export const Conditions = ({
  isExpandedView,
  conditions,
  tiers,
  setExpandedView,
  errors,
  isEdit,
}: Props) => {
  const classes = useStyles();
  const { message } = useMessage();
  const { currency } = useAppSelector(selectCurrentStoreData);
  const defaultLang = useAppSelector(selectDefaultLanguageData);

  const prepareConditionInputName = (
    attribute: string,
    wallet?: string,
    event?: string,
  ) => {
    switch (attribute) {
      case "activeUnits":
        return `${message({ id: "activeUnits" })} (${wallet})`;
      case "totalEarnedUnits":
        return `${message({ id: "totalEarnedUnitsConst" })} (${wallet})`;
      case "numberOfCustomEvents":
        return `${message({ id: "numberOf" })} ${event}`;
      case "monthsSinceJoiningProgram":
        return message({ id: "monthsSinceJoiningTheProgram" });
      case "totalSpending":
        return `${message({ id: "totalSpendingWithCurrency" }, { currency })}`;
      case "cumulatedEarnedUnits":
        return `${message({ id: "cumulativeEarnedUnits" })} (${wallet})`;
      default:
        return message({ id: attribute });
    }
  };

  const findTierName = (tier: TierDetailsType) => {
    if (!defaultLang) {
      return tier.name;
    }

    const matchedLang = tier.translations[defaultLang.code];

    return matchedLang?.name || tier.name;
  };

  return (
    <OLPaper
      className={`${classes.OLPaper}  ${
        isExpandedView ? classes.expandedView : ""
      }`}
    >
      {isExpandedView && (
        <div className={classes.conditionsHeader}>
          <Typography className={classes.title}>
            {message({ id: "tiersConditions" })}
          </Typography>
          <span className={classes.closeIconWrapper}>
            <XCloseIcon onClick={() => setExpandedView(false)} />
          </span>
        </div>
      )}
      {isEdit && (
        <Typography className={classes.warning}>
          {message({ id: "warningEditTiers" })}
        </Typography>
      )}
      <div className={classes.wrapper}>
        {tiers.map((tier, tierIndex) => {
          const tierName = findTierName(tier);
          const tierError = errors?.[tierIndex];
          const generalError =
            tierError &&
            Object.values(tierError.conditions || {}).find((err) => {
              return Array.isArray(err);
            });

          return (
            <>
              <Typography className={classes.sectionHeader} mb={12}>
                {tierName}
              </Typography>
              {generalError && (
                <Typography className={classes.errorGeneral}>
                  {Object.values(generalError)}
                </Typography>
              )}
              <Grid
                container
                spacing={8}
                mb={12}
                className={classes.inputWrapper}
              >
                {conditions.map((item, index: number) => {
                  return (
                    <Grid key={index} item xs={2.5}>
                      <TextInput
                        data-test-id={`tiers-${tierIndex}-conditions-${index}-value`}
                        label={
                          <RequiredFieldLabel
                            text={prepareConditionInputName(
                              item.attribute,
                              item.walletType,
                              item.eventName,
                            )}
                          />
                        }
                        name={`tiers[${tierIndex}].conditions[${index}].value`}
                        disabled={tier.isDefault}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            </>
          );
        })}
      </div>
    </OLPaper>
  );
};
